import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { ArrowLeft } from "lucide-react";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { renderHourTick } from "../../../../components/recharts/components";
import { useDispatch, useSelector } from "react-redux";
import FilterMenu from "../../../../components/FilterMenu";
import { setFilterWidth } from "../../../../store/state/FilterWidth";
import { R_CHART_TYPES } from "../../const";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";
let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  // {
  //   label: "Generator",
  //   key: "generator",
  //   values: ["generator"],
  //   isTime: false,
  // },
];
let VARIABLE = ["Buy Bid", "Sell Bid"];

const chartSuffixes = [
  {
    suffix: "price",
    displayName: "Price",
    stackSuffix: "price",
    chartType: ChartType.Line,
    stroke: "rgba(255, 171, 121, 1)",
    legendColor: "rgba(255, 171, 121, 1)",
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "quantum",
    displayName: "Quantum",
    stackSuffix: "Quantum",
    chartType: ChartType.Bar,
    stroke: "rgba(132, 187, 199, 1)",
    legendColor: "rgba(132, 187, 199, 1)",
    unit: "MW",
    yAxisId: "left",
  },
];

export default function BidChart({
  selectedBlock,
  selectedGeneratorName,
  data,
}: {
  selectedBlock: any[];
  selectedGeneratorName: any[];
  data: any[];
}) {
  const [open, setOpen] = useState(true);
  const [fullbtn, setFullBtn] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
  const [selectedVariable, setSelectedVariable] = useState(VARIABLE[0]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[0]);
  const preSelectedDate = useSelector(
    (state: any) => state.modGridcoDate.selectedDate
  );
  
  const filteredChartTypes = R_CHART_TYPES.filter(
    (item) => item.label !== "Stacked Bar"
  );
  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      date: preSelectedDate,
      price: item.price || 0,
    }));

    if (selectedVariable) {
      filteredData = filteredData.filter((item) =>
        selectedVariable === "Buy Bid" ? item.isBuy : !item.isBuy
      );
    }

    // Filter by Block selectiona
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock
          .map((b) => Number(b.value))
          .includes(Number(item.time_slot__slot))
      );
    }

    if (selectedGeneratorName.length > 0) {
      const generator = selectedGeneratorName.map((o: any) => o.value ?? o);
      filteredData = filteredData.filter((row) =>
        generator.includes(row.generator)
      );
    }
    const par: ChartDataParams = {
      keys: ["time_slot__slot", "date", "month"],
      tableKeys: ["time_slot__slot", "date", "month"],
      groupBy: [],
      average: true,
      volumeKey: ``,
      aggregationKeys: ["quantum", "price"],
      averageKeys: ["price"],
      averageDuplicationKeys: ["time_slot__slot", "date", "bid"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    preSelectedDate,
    selectedBlock,
    selectedGeneratorName,
    selectedMetrics,
    selectedVariable,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      type: suffix === "price" ? ChartType.Line : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      legendColor: legendColor,
      yAxisId: yAxisId,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        stroke,
        legendColor,
        yAxisId,
      }) =>
        chartKeys.map((key, index) =>
          createChartObject(
            key,
            index,
            suffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            stroke,
            legendColor,
            yAxisId
          )
        )
    );
  }

  const handleWidthChange = useMemo(
    () => (width: number) => {
      if (open) {
        const roundedWidth = Math.ceil(width + 30);
        const pixelValue = `${roundedWidth}px`;
        const pageWidth = `calc(100% - ${pixelValue} - 35px)`;
        dispatch(setFilterWidth(pageWidth));
      }
    },
    [dispatch, open]
  );

  // Create a debounced version that we'll use in the observer
  const debouncedHandleWidthChange = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<typeof handleWidthChange>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        handleWidthChange(...args);
        timeoutId = null;
      }, 100);
    };
  }, [handleWidthChange]);

  // Set up ResizeObserver with debounce
  useEffect(() => {
    if (!containerRef.current) return;
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        debouncedHandleWidthChange(entry.contentRect.width);
      }
    });

    resizeObserver.observe(currentContainer);

    return () => {
      resizeObserver.unobserve(currentContainer);
    };
  }, [debouncedHandleWidthChange]);

  // Handle open/close state changes
  useEffect(() => {
    if (!open) {
      const pageWidth = `calc(100% - 70px)`;
      dispatch(setFilterWidth(pageWidth));
    }
    // When opening, the ResizeObserver will handle the width change
  }, [open, dispatch]);
  return (
    <>
      <div className="flex max-h-[calc(100vh-300px)]">
        <div className="h-100 border border-[#718EBF1C] rounded-lg shadow-[rgba(149,157,165,0.2)_0px_8px_24px] ">
          <div className="hide-btn-outer">
            {/* <button
              style={{
                width: fullbtn ? "100px" : "30px",
                overflow: "hidden",
                height: "30px",
                [open ? "right" : "left"]: open ? "-7px" : "20px",
              }}
              onMouseOver={() => setFullBtn(true)}
              onMouseLeave={() => setFullBtn(false)}
              onClick={() => setOpen(!open)}
              className="hide-btn flex items-center justify-between gap-1 transition-all text-nowrap duration-700 ease-in-out"
            >
              <ArrowLeft
                size={18}
                className={`text-primary transition-transform duration-300 ${
                  !open ? "rotate-180" : ""
                }`}
              />
              {fullbtn && (
                <>
                  {open ? <span>Hide Filters</span> : <span>Show Filters</span>}
                </>
              )}
            </button> */}
            <div
              ref={containerRef}
              className={`transition-all  duration-700 ease-in-out bg-white m-[0.8rem] me-1 rounded-2xl`}
              style={{
                width: open ? "auto" : "0px",
                height: "calc(100vh - 328px)",
                transition: "all 0.3s ease-in-out, opacity 0.3s ease-in-out",
                padding: open ? "0px" : "12px",
                paddingRight: open ? "1rem" : "12px",
              }}
            >
              <div
                className="rounded-2xl"
                style={{
                  height: "calc(100vh - 328px)",
                  overflowY: open ? "auto" : "hidden",
                }}
              >
                <div
                  style={{
                    opacity: open ? 1 : 0,
                  }}
                  className="transition-all duration-700 ease-in-out"
                >
                  <div className="side-filters-container text-nowrap">
                    <FilterMenu
                      title="X Axis"
                      items={X_AXIS.map((item) => item.label)}
                      selectedItems={[selectedInterval.label]}
                      onSelect={(item, index) => {
                        setSelectedInterval(X_AXIS[index]);
                      }}
                    />
                    <FilterMenu
                      title="Variable"
                      items={VARIABLE.map((item) => item)}
                      selectedItems={[selectedVariable]}
                      onSelect={(item, index) => {
                        setSelectedVariable(VARIABLE[index]);
                      }}
                    />

                    <FilterMenu
                      title="Chart Type"
                      items={filteredChartTypes.map((item) => item.label)}
                      selectedItems={[selectedChartType.label]}
                      onSelect={(item, index) => {
                        setSelectedChartType(filteredChartTypes[index]);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 h-100 ml-4 shadow-[rgba(149,157,165,0.2)_0px_8px_24px]">
          <div className="dash-card col-span-1 md:col-span-6 border border-[#718EBF1C] !bg-[] !shadow-none !h-full w-full">
            <ReMixChart
              chartHeight="calc(100% - 50px)"
              data={chartData}
              showBrush={true}
              brushStart={BrushStart.Start}
              isTimeSlot={true}
              onlyTitle={true}
              legends={getLegends()}
              yAxisLabel="MW"
              secondYAxisLabel="Rs/KWh"
              nameGap={40}
              xDataKey={"time_slot__slot"}
              top={10} // Top margin for grid
              bottom={30} // Bottom margin for grid
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={"date"}
              showToolbox={false}
            />
          </div>
        </div>
      </div>
    </>
  );
}
